/**
 * Resolve the bearer token used for the agent transport, in priority order:
 *   1. Office SSO  — `OfficeRuntime.auth.getAccessToken()` (modern Office Identity API).
 *      Requires a manifest `WebApplicationInfo` block + an Azure AD app registration
 *      (see README). When that is not configured the call rejects and we fall back.
 *   2. Build-time env token — `VITE_AI_AUTH_TOKEN`.
 *   3. `localStorage.token` — when the add-in shares storage with a signed-in web app.
 *
 * Returns the raw token (without the `Bearer ` prefix); callers set the
 * `Authorization` header. Returns null when no token is available.
 */
export async function resolveAuthToken(): Promise<string | null> {
  const ssoToken = await tryOfficeSso()
  if (ssoToken) return ssoToken

  const envToken = import.meta.env.VITE_AI_AUTH_TOKEN
  if (envToken) return envToken

  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem('token')
    if (stored) return stored
  }

  return null
}

interface OfficeAuthLike {
  auth?: {
    getAccessToken?: (options?: {
      allowSignInPrompt?: boolean
      allowConsentPrompt?: boolean
    }) => Promise<string>
  }
}

async function tryOfficeSso(): Promise<string | null> {
  try {
    // `OfficeRuntime` is present in modern Office hosts; guard for plain-browser dev.
    const runtime = (globalThis as Record<string, unknown>).OfficeRuntime as OfficeAuthLike | undefined
    if (!runtime?.auth?.getAccessToken) return null
    const token = await runtime.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true
    })
    return token || null
  } catch {
    // Not configured (no WebApplicationInfo / Azure app) or user declined — fall back.
    return null
  }
}
